import { useMemo } from "react";
import { Dayjs } from "dayjs";
import { getReadyDateDetails } from "../configs/checkoutDateRules";
import { useAppSelector } from "./reduxHooks";
import { IDateHookResult } from "./useSpecialDate";

const defaultWorkingDays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const parseTime = (time: string) => {
  const [hours, minutes, seconds] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, seconds, 0);
  return date;
};

export const useRegularDate = (): IDateHookResult => {
  const storeConfig = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );
  const storeConfigError = useAppSelector(
    (state) => state.store.getStoreConfigError
  );

  const isReady = !!storeConfig || !!storeConfigError;

  // Worked out on every render (cheap) so it follows the clock - a POS left
  // open past the cut-off or overnight kept showing the old dates. Memoised on
  // the value so its identity only changes when the date does.
  const body = storeConfig?.body;
  const details = getReadyDateDetails({
    isSpecialDay: false,
    checkInCutOffTime: parseTime(body?.cutOffTime || "10:00:00"),
    pickupTime: parseTime(body?.pickupTime || "15:00:00"),
    workingDays: body?.workingDays || defaultWorkingDays,
    generalServiceDays: body?.daysToDeliver ?? 2,
    specialServiceDays: body?.specialDaysToDeliver ?? 0,
  });
  const fresh = details.date;
  const freshKey = fresh.format("YYYY-MM-DD HH:mm");
  // Holidays it skipped, for the pin tag modal's explanation (3.f)
  const holidaysKey = details.holidays.map((h) => h.date).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const holidays = useMemo(() => details.holidays, [holidaysKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const regularDate: Dayjs = useMemo(() => fresh, [freshKey]);

  return {
    date: regularDate,
    isReady,
    holidays,
    isAdminDate: details.isAdminDate,
  };
};
